
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContentManager } from '../../hooks/useContentManager';

interface PinnacleSlide {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  button_text: string;
  button_url: string;
  image: string;
  bg_image: string;
  order: number;
}

export default function PinnacleProject() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { content } = useContentManager();
  const navigate = useNavigate();

  // Supabase'den Pinnacle slider verilerini al
  const pinnacleSliderContent = content.find(item => 
    item.page === 'home' && 
    item.section === 'pinnacle-slider' && 
    item.type === 'slides'
  );
  
  // Admin panelden eklenen slide'ları al
  let pinnacleSlides: PinnacleSlide[] = [];
  if (pinnacleSliderContent?.metadata?.slides) {
    pinnacleSlides = pinnacleSliderContent.metadata.slides
      .map((slide: any) => ({
        id: slide.id,
        title: slide.title,
        subtitle: slide.subtitle,
        description: slide.description,
        button_text: slide.button_text || '',
        button_url: slide.button_url || '',
        image: slide.image,
        bg_image: slide.bg_image,
        order: slide.order || 0
      }))
      .sort((a: PinnacleSlide, b: PinnacleSlide) => a.order - b.order);
  }

  // Varsayılan veriler (Supabase'de veri yoksa)
  const defaultSlides: PinnacleSlide[] = [
    {
      id: '1',
      title: 'Golden Tower',
      subtitle: 'Luxury Living',
      description: 'Al Jaddaf',
      button_text: 'Discover the Collection',
      button_url: '/collections',
      image: '',
      bg_image: '',
      order: 0
    },
    {
      id: '2',
      title: 'Golden Tower',
      subtitle: 'Premium Collection',
      description: 'Downtown Dubai',
      button_text: 'Discover the Collection',
      button_url: '/collections',
      image: '',
      bg_image: '',
      order: 1
    },
    {
      id: '3',
      title: 'Golden Tower',
      subtitle: 'Exclusive Residences',
      description: 'Jumeirah Village Triangle',
      button_text: 'Discover the Collection',
      button_url: '/collections',
      image: '',
      bg_image: '',
      order: 2
    },
    {
      id: '4',
      title: 'Golden Tower',
      subtitle: 'Sky Collection',
      description: 'Al Jaddaf',
      button_text: 'Discover the Collection',
      button_url: '/collections',
      image: '',
      bg_image: '',
      order: 3
    },
    {
      id: '5',
      title: 'Golden Tower',
      subtitle: 'Elite Living',
      description: 'JVC',
      button_text: 'Discover the Collection',
      button_url: '/collections',
      image: '',
      bg_image: '',
      order: 4
    }
  ];

  // Admin panelden veri varsa onu kullan, yoksa varsayılan verileri kullan
  const isAdminSlides = pinnacleSlides.length > 0;
  const slides = isAdminSlides ? pinnacleSlides : defaultSlides;

  useEffect(() => {
    if (slides.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleButtonClick = (url: string) => {
    navigate(url);
  };

  if (slides.length === 0) return null;

  return (
    <section className="relative h-screen w-full overflow-hidden bg-black">
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          <div className="absolute inset-0">
            {slide.bg_image && (
              <img
                src={slide.bg_image}
                alt={slide.subtitle}
                className="w-full h-full object-cover object-top"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent"></div>
          </div>
        </div>
      ))}

      <div className="relative h-full flex items-center">
        <div className="w-full max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white z-20">
              {slides.map((slide, index) => (
                <div
                  key={slide.id}
                  className={`transition-opacity duration-500 ${
                    index === currentIndex ? 'opacity-100' : 'opacity-0 absolute'
                  }`}
                >
                  <h2 className="text-5xl md:text-7xl mb-4 font-light tracking-wide">
                    {slide.title}
                  </h2>
                  <h3 className="text-6xl md:text-8xl mb-6 font-bold tracking-wider">
                    {slide.subtitle}
                  </h3>
                  <h4 className="text-xl md:text-2xl mb-8 text-gray-300 tracking-widest">
                    {slide.description}
                  </h4>
                  {isAdminSlides && slide.button_text?.trim() && slide.button_url?.trim() && (
                    <button
                      onClick={() => handleButtonClick(slide.button_url)}
                      className="inline-block px-8 py-3 border border-white text-white hover:bg-white hover:text-black transition-all duration-300 text-sm tracking-widest whitespace-nowrap cursor-pointer"
                    >
                      {slide.button_text}
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="relative z-20">
              {slides.map((slide, index) => (
                <div
                  key={slide.id}
                  className={`transition-opacity duration-500 ${
                    index === currentIndex ? 'opacity-100' : 'opacity-0 absolute inset-0'
                  }`}
                >
                  {slide.image && (
                    <img
                      src={slide.image}
                      alt={slide.subtitle}
                      className="w-full h-auto object-top"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={goToPrev}
        className="absolute left-8 top-1/2 -translate-y-1/2 z-30 text-white text-4xl hover:text-gray-300 transition-colors cursor-pointer"
        aria-label="Previous"
      >
        ‹
      </button>
      <button
        onClick={goToNext}
        className="absolute right-8 top-1/2 -translate-y-1/2 z-30 text-white text-4xl hover:text-gray-300 transition-colors cursor-pointer"
        aria-label="Next"
      >
        ›
      </button>
    </section>
  );
}
